import { FC, useState, useEffect } from 'react';
import { Modal, ModalContent, Button } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { FaTimes, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useEvent } from '~/hooks/useEvent';
import Image from 'next/image';
import { isVideo } from '~/utils/media';

export interface ZoomableMedia {
  id: number | string;
  url: string;
  type?: 'image' | 'video';
}

interface ImageZoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  media: ZoomableMedia[];
  initialIndex?: number;
  title?: string;
}

export const ImageZoomModal: FC<ImageZoomModalProps> = ({
  isOpen,
  onClose,
  media,
  initialIndex = 0,
  title = '',
}) => {
  const { t } = useTranslation();
  const { sendEvent } = useEvent();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  const handleClose = () => {
    sendEvent('media_viewer_close', {
      totalImages: media.length,
      currentIndex
    });
    onClose();
  };

  const nextImage = () => {
    if (currentIndex < media.length - 1) {
      sendEvent('media_viewer_next', {
        fromIndex: currentIndex,
        totalImages: media.length
      });
      setCurrentIndex(currentIndex + 1);
    }
  };

  const prevImage = () => {
    if (currentIndex > 0) {
      sendEvent('media_viewer_prev', {
        fromIndex: currentIndex,
        totalImages: media.length
      });
      setCurrentIndex(currentIndex - 1);
    }
  };

  const currentItem = media?.[currentIndex];
  const isCurrentVideo = currentItem && isVideo(currentItem.url);

  // Convert t() results to string to fix TypeScript errors
  const closeLabel = String(t('common.close'));
  const prevLabel = String(t('common.previous'));
  const nextLabel = String(t('common.next'));

  if (!media || media.length === 0) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      scrollBehavior="inside"
      hideCloseButton
      size="full"
    >
      <ModalContent className="bg-secondary-100 bg-opacity-90 p-0">
        <div className="absolute top-4 right-4 z-50 flex gap-2">
          <Button
            isIconOnly
            variant="solid"
            className="bg-secondary-700 text-white"
            onPress={handleClose}
            aria-label={closeLabel}
          >
            <FaTimes />
          </Button>
        </div>

        <div className="absolute top-1/2 left-4 z-50">
          {currentIndex > 0 && (
            <Button
              isIconOnly
              variant="solid"
              onPress={prevImage}
              aria-label={prevLabel}
            >
              <FaArrowLeft />
            </Button>
          )}
        </div>

        <div className="absolute top-1/2 right-4 z-50">
          {currentIndex < media.length - 1 && (
            <Button
              isIconOnly
              variant="solid"
              onPress={nextImage}
              aria-label={nextLabel}
            >
              <FaArrowRight />
            </Button>
          )}
        </div>

        <div className="w-full h-screen flex items-center justify-center overflow-hidden">
          {currentItem && (
            isCurrentVideo ? (
              <video
                src={`${currentItem.url}#t=0.01`}
                controls
                className="max-h-full max-w-full"
                autoPlay
              />
            ) : (
              <div className="relative h-full w-full flex items-center justify-center overflow-hidden">
                <Image
                  src={currentItem.url}
                  alt={`${title} ${currentIndex + 1}`}
                  className="max-h-full max-w-full object-contain"
                  fill
                  sizes="100vw"
                  priority
                />
              </div>
            )
          )}
        </div>

        {media.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center">
            <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full">
              {currentIndex + 1} / {media.length}
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ImageZoomModal; 